

import { CardTransactionType, PaymentDTO } from 'core/api';
import { CardTransaction, CashTransaction, getReversalTransactionType, getTransactionAmount, paymentRequiresCardProcessor, paymentRequiresCashControl, Transaction } from './ExternalPaymentsService.Common';
import { TransactionList } from './ExternalPaymentService.TransactionList';
import { RefundPaymentsScreen } from './RefundExternalPaymentService.RefundPaymentsScreen';
import { WarnBeforeUndoingScreen } from './ExternalPaymentsService.WarnBeforeUndoingScreen';
import { Modal } from 'core/ui-next/modal';
import { Result } from 'core/infrastructure';

export default class RefundExternalPaymentService {

	private readonly completedTransactions: Transaction[];

	constructor(
		private readonly payments: PaymentDTO[],
		private readonly transactionList: TransactionList,
		private readonly ticketId: Nullable<string>
	) {
		this.completedTransactions = [];
	}

	public async revert(payment: PaymentDTO): Promise<{ ok: boolean, refundPayment: PaymentDTO }> {

		const cardTransaction = paymentRequiresCardProcessor(payment) && payment.CardTransaction.TransactionId.length > 0
			? this.transactionList.findTransaction('CardProcessor', getTransactionAmount(payment), payment.CardTransaction.XmlDetails)
			: null;

		if (cardTransaction) {
			if (!this.ticketId)
				throw new Error('No se pueden procesar pagos con tarjeta si no hay un ticketGlobalId asignado');

			const result = await this.transactionList.removeCardTransaction(cardTransaction.amount, cardTransaction.xmlDetails);

			if (result.ok) {
				this.completedTransactions.push({ ...cardTransaction, amount: -cardTransaction.amount, type: getReversalTransactionType(cardTransaction.type) });
				return { ok: true, refundPayment: { ...payment, CardTransaction: result.value } };
			}

			return { ok: false, refundPayment: { ...payment } };
		}

		// Quito en el RefundPayment el transaction por si esta forma de pago anteriormente lo llevaba. De esta manera al guardar en la base de datos no daría
		// error.  
		const emptyCardTransaction = { TransactionId: '', TransactionType: CardTransactionType.Adyen, XmlDetails: '' };
		const refundPayment = { ...payment, CardTransaction: emptyCardTransaction };

		const cashTransaction = paymentRequiresCashControl(payment)
			? this.transactionList.findTransaction('CashControl', payment.Amount)
			: null;

		if (cashTransaction) {
			const result = await this.transactionList.removeCashTransaction(this.ticketId, cashTransaction.amount);

			if (result.ok)
				this.completedTransactions.push({ ...cashTransaction, amount: -cashTransaction.amount, type: getReversalTransactionType(cashTransaction.type) });

			return { ok: result.ok, refundPayment };
		}

		return { ok: true, refundPayment };
	}

	public async discard(payment: PaymentDTO): Promise<void> {

		const cardTransaction = paymentRequiresCardProcessor(payment) && payment.CardTransaction.TransactionId.length > 0
			? this.transactionList.findTransaction('CardProcessor', getTransactionAmount(payment), payment.CardTransaction.XmlDetails)
			: null;

		const cashTransaction = paymentRequiresCashControl(payment)
			? this.transactionList.findTransaction('CashControl', payment.Amount)
			: null;

		if (cardTransaction) {
			await this.transactionList.discardCardPayment(this.ticketId, cardTransaction);
		}

		if (cashTransaction) {
			await this.transactionList.discardCashPayment(this.ticketId, cashTransaction);
		}
	}

	public async refund(): Promise<Result<PaymentDTO[]>> {

		// Si no tengo pagos devuelvo un Array<PaymentDTO> vacío, 
		// ya que no se van a procesar pagos de devolución en la ventana de RefundPaymentsScreen. 
		if (this.payments.length === 0)
			return Result.ok([]);

		return await Modal.show(RefundPaymentsScreen, { revertPayment: (payment) => this.revert(payment), discardPayment: (payment) => this.discard(payment), payments: this.payments });
	}

	public async undoAll(): Promise<void> {

		const revertTransactionList = new TransactionList(getReversalTransactionType(this.transactionList.getType()), 'REFUND', this.completedTransactions);
		if (revertTransactionList.hasExternalTransactions()) {
			if (!this.ticketId)
				throw new Error('No se pueden procesar pagos con tarjeta si no hay un ticketGlobalId asignado');
			await WarnBeforeUndoingScreen.warn(revertTransactionList.getType(), 'REFUND', revertTransactionList.getExternalTransactions());
			await revertTransactionList.undoAll(this.ticketId);
		}
	}

	static for(type: 'Refund' | 'PreRefund' | 'RemoveTip', ticketGlobalId: Nullable<string>, payments: PaymentDTO[]): RefundExternalPaymentService {

		const registeredPreviousDirection = 'PAY';
		const registeredPreviousType = getReversalTransactionType(type);

		const cardTransactions: CardTransaction[] = payments
			.filter(x => paymentRequiresCardProcessor(x) && x.CardTransaction.TransactionId.length > 0)
			.map(x => ({
				amount: getTransactionAmount(x),
				device: 'CardProcessor',
				type: registeredPreviousType,
				xmlDetails: x.CardTransaction.XmlDetails
			}));

		const cashTransactions: CashTransaction[] = payments
			.filter(x => paymentRequiresCashControl(x))
			.map(x => ({
				amount: getTransactionAmount(x),
				device: 'CashControl',
				type: registeredPreviousType
			}));


		const transactionList = new TransactionList(registeredPreviousType, registeredPreviousDirection, [...cardTransactions, ...cashTransactions]);
		return new RefundExternalPaymentService(payments, transactionList, ticketGlobalId);
	}
}

